'use client';

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter, usePathname } from 'next/navigation';
import { fetchProjects, deleteProject } from '@/lib/api';
import { useAppStore } from '@/lib/store';
import { Project } from '@/lib/types';
import { getPermissions } from '@/lib/permissions';
import { Navigation } from '@/components/Navigation';
import { Home } from '@/components/Home';
import { SAMPLE_PROJECTS } from '@/lib/sampleData';
import { useAuth } from '@/components/auth/AuthProvider';
import { InactiveUserMessage } from '@/components/auth/InactiveUserMessage';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { createClient } from '@/lib/supabase/client';

// 懒加载组件 - 只在需要时加载代码
const Dashboard = dynamic(() => import('@/components/Dashboard').then(mod => ({ default: mod.Dashboard })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

const CRAnalysis = dynamic(() => import('@/components/CRAnalysis').then(mod => ({ default: mod.CRAnalysis })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

const RadarCompare = dynamic(() => import('@/components/RadarCompare').then(mod => ({ default: mod.RadarCompare })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

const StrategyFunnel = dynamic(() => import('@/components/StrategyFunnel').then(mod => ({ default: mod.StrategyFunnel })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

const ScoringAssessment = dynamic(() => import('@/components/ScoringAssessment').then(mod => ({ default: mod.ScoringAssessment })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

const ProjectList = dynamic(() => import('@/components/ProjectList').then(mod => ({ default: mod.ProjectList })), {
  loading: () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-slate-500">加载中...</div>
    </div>
  ),
});

export default function HomePage() {
  const { activeTab, lang, setActiveTab, userRole, getPermissions: getStorePermissions } = useAppStore();
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const supabase = useMemo(() => createClient(), []);

  // 判断哪些标签页需要项目数据
  const needsProjects = ['dashboard', 'cr_analysis', 'radar', 'strategy', 'list'].includes(activeTab);

  // 判断用户是否可以查询数据（已认证且已激活）
  const canFetchProjects = !authLoading && !!user && user.is_active !== false;

  // 所有 hooks 必须在条件返回之前调用
  const { data: projects = [], isLoading, error } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    enabled: needsProjects && canFetchProjects, // 只在需要时查询，且用户已认证并激活
    // 为projects查询添加详细的缓存配置，提升性能
    staleTime: 5 * 60 * 1000, // 5分钟 - 数据保持新鲜，减少查询频率
    gcTime: 10 * 60 * 1000, // 10分钟 - 缓存保留时间，即使组件卸载也保留
    // 启用挂载时重新获取，确保数据最新
    refetchOnMount: true, // 组件挂载时重新获取，避免使用过期的空数组缓存
    refetchOnWindowFocus: false, // 窗口聚焦时不重新获取
    // React Query 默认支持请求去重，相同queryKey的请求会自动合并
  });

  // 全局认证检查：如果用户未登录，自动跳转到登录页
  // 使用 session 检测避免 profile 加载延迟导致的误判
  useEffect(() => {
    // 排除登录页面和认证回调页面，避免循环重定向
    const publicPaths = ['/auth/login', '/auth/callback'];
    const isPublicPath = publicPaths.some(path => pathname?.startsWith(path));

    if (isPublicPath) return;
    if (authLoading) return;

    let isCancelled = false;

    const checkSessionAndRedirect = async () => {
      if (user) {
        if (!isCancelled) {
          setHasSession(true);
          setSessionChecked(true);
          console.log('[HomePage] User authenticated:', user.id);
        }
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (isCancelled) return;

        const sessionExists = !!session?.user;
        setHasSession(sessionExists);
        setSessionChecked(true);

        if (sessionExists) {
          console.log('[HomePage] Session exists, waiting for profile to load...');
          return;
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn('[HomePage] Failed to check session, redirecting to login...', error);
          setHasSession(false);
          setSessionChecked(true);
        }
      }

      if (!isCancelled) {
        console.log('[HomePage] User not authenticated, redirecting to login...');
        router.push('/auth/login');
      }
    };

    checkSessionAndRedirect();

    return () => {
      isCancelled = true;
    };
  }, [user, authLoading, pathname, router, supabase]);

  // 监听用户状态变化，清除缓存并重新查询
  useEffect(() => {
    if (canFetchProjects && needsProjects) {
      // 用户认证完成且已激活，清除缓存并重新查询
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    }
  }, [canFetchProjects, needsProjects, queryClient]);

  // 如果用户未登录且不在加载中，显示加载状态
  // 注意：这个条件返回必须在所有 hooks 之后
  if (!authLoading && !user) {
    const message = sessionChecked
      ? (hasSession ? '正在加载用户信息...' : '正在跳转到登录页...')
      : '正在检查登录状态...';
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">{message}</div>
      </div>
    );
  }

  // 如果数据库为空，使用示例数据（用于测试数据点颜色效果）
  const displayProjects = projects.length > 0 ? projects : SAMPLE_PROJECTS;

  const handleSeedSampleData = async () => {
    if (!confirm('确定要插入示例数据到数据库吗？这将添加 10 个示例项目。')) {
      return;
    }

    try {
      const response = await fetch('/api/seed-sample-data', {
        method: 'POST',
      });

      const result = await response.json();

      if (response.ok) {
        alert(`成功！${result.message}`);
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      } else {
        alert(`插入失败：${result.error}`);
      }
    } catch (err: any) {
      console.error('插入示例数据失败:', err);
      alert(`插入失败：${err.message || '未知错误'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('确认删除此项目？')) {
      try {
        await deleteProject(id);
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      } catch (err) {
        console.error('删除失败:', err);
        alert('删除失败，请重试');
      }
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setActiveTab('form'); // 切换到表单标签页
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
  };

  const handleSaveComplete = () => {
    setEditingProject(null);
    setActiveTab('list'); // 保存后切换到列表页
  };

  // 获取权限
  const permissions = getStorePermissions();

  // 检查用户激活状态（只有当 is_active 明确为 false 时才视为未激活）
  const isUserInactive = user && user.is_active === false;

  // 如果用户未激活且需要项目数据，显示等待激活消息
  if (!authLoading && isUserInactive && needsProjects) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <InactiveUserMessage />
        </main>
      </div>
    );
  }

  // 只在需要项目数据的标签页显示加载状态
  if (needsProjects && isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">加载中...</div>
      </div>
    );
  }

  if (needsProjects && error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-red-500">加载失败，请检查数据库连接</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
        {needsProjects && <OfflineIndicator lang={lang} />}
        {needsProjects && projects.length < 5 && !isLoading && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 text-sm text-blue-700">
                <strong>提示：</strong>
                {projects.length === 0 ? (
                  <span>当前使用示例数据展示效果。</span>
                ) : (
                  <span>当前数据库只有 {projects.length} 个项目。为了查看所有 9 个区域的数据点颜色效果，建议插入示例数据。</span>
                )}
                <div className="mt-2 text-xs">
                  <strong>数据点颜色方案：</strong>
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    <li>Zone 1 (红色背景) → 红色数据点</li>
                    <li>Zone 2 (蓝色背景) → 蓝色数据点</li>
                    <li>Zone 3 (黄色背景) → 黄色数据点</li>
                    <li>Zone 4 (青色背景) → 青色数据点</li>
                    <li>Zone 5 (紫色背景) → 紫色数据点</li>
                    <li>Zone 6 (橙色背景) → 橙色数据点</li>
                    <li>Zone 7 (黄绿背景) → 黄绿数据点</li>
                    <li>Zone 8 (琥珀背景) → 琥珀数据点</li>
                    <li>Zone 9 (绿色背景) → 绿色数据点</li>
                  </ul>
                </div>
              </div>
              <button
                onClick={handleSeedSampleData}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                插入示例数据
              </button>
            </div>
          </div>
        )}
        {activeTab === 'home' && (
          <Home lang={lang} />
        )}
        {activeTab === 'dashboard' && (
          <Dashboard projects={displayProjects} lang={lang} />
        )}
        {activeTab === 'cr_analysis' && (
          <CRAnalysis projects={displayProjects} lang={lang} />
        )}
        {activeTab === 'radar' && (
          <RadarCompare projects={displayProjects} lang={lang} />
        )}
        {activeTab === 'strategy' && (
          <StrategyFunnel projects={displayProjects} lang={lang} />
        )}
        {activeTab === 'form' && (
          <ScoringAssessment
            lang={lang}
            projectId={editingProject?.id}
            initialData={editingProject || undefined}
            onSave={handleSaveComplete}
            onCancel={editingProject ? handleCancelEdit : undefined}
          />
        )}
        {activeTab === 'list' && (
          <ProjectList
            projects={displayProjects}
            lang={lang}
            onDelete={handleDelete}
            onEdit={handleEdit}
            canEdit={permissions.canEdit}
            canDelete={permissions.canDelete}
          />
        )}
      </main>
    </div>
  );
}

